import { useMemo, useState } from "react";
import { Button, LabInvoicePdfFrame, showError, showSuccess } from "@common";
import {
  useLabInvoiceAttachments,
  useLabInvoiceAttachmentViewUrl,
  useUpdateLabInvoiceAttachmentAssignment,
} from "@api/hooks";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { formatMoney } from "@utils/format";

function AttachmentChip({ attachment, isActive, onSelect }) {
  const needsReview = attachment.scan_status === "manual_review";
  const isApplied = attachment.lab_cost_status === "applied";
  const isSuggested = attachment.lab_cost_status === "suggested";
  const suggestedBreakdownTotal = (attachment.breakdowns || [])
    .filter((breakdown) => breakdown.status === "suggested")
    .reduce((total, breakdown) => total + Number(breakdown.total_amount || 0), 0);
  const suggestedAmount = suggestedBreakdownTotal || Number(attachment.extracted_lab_cost || 0);
  const senderLabel = attachment.sender_name || attachment.sender_email || "unknown";
  const practitionerNames = [
    ...new Set(
      (attachment.breakdowns || [])
        .map((breakdown) => breakdown.matched_practitioner_name || breakdown.practitioner_name)
        .filter(Boolean),
    ),
  ];
  const practitionerLabel =
    practitionerNames.length > 0
      ? practitionerNames.join(", ")
      : attachment.matched_practitioner_name || attachment.practitioner_name || null;

  return (
    <button
      type="button"
      onClick={() => onSelect(attachment)}
      className={`group flex w-56 shrink-0 flex-col gap-1 rounded-md border-2 bg-white p-2 text-left shadow-sm transition hover:border-palette-indigo-label hover:shadow-md ${
        isActive ? "border-palette-indigo-label" : "border-palette-slate-divider"
      }`}
      title={attachment.original_filename}
    >
      <div className="flex items-center gap-1.5">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="h-4 w-4 shrink-0 text-palette-rose-hover"
          aria-hidden="true"
        >
          <path d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8.83a2 2 0 0 0-.59-1.41l-4.83-4.83A2 2 0 0 0 13.17 2H6Zm7 1.5L18.5 9H14a1 1 0 0 1-1-1V3.5Z" />
        </svg>
        <span className="truncate text-xs font-medium text-palette-slate-primary">
          {attachment.original_filename}
        </span>
      </div>

      {(needsReview || isSuggested || isApplied) && (
        <div className="flex flex-wrap gap-1">
          {needsReview && (
            <span className="rounded bg-palette-amber-muted px-1.5 py-0.5 text-[10px] font-medium text-palette-amber-text">
              Check
            </span>
          )}
          {isSuggested && (
            <span className="rounded bg-palette-emerald-muted px-1.5 py-0.5 text-[10px] font-medium text-palette-emerald-text">
              {formatMoney(suggestedAmount)}
            </span>
          )}
          {isApplied && (
            <span className="rounded bg-palette-slate-muted px-1.5 py-0.5 text-[10px] font-medium text-palette-slate-badge">
              Applied
            </span>
          )}
        </div>
      )}

      <div className="truncate text-[11px] text-palette-slate-secondary" title={senderLabel}>
        {senderLabel}
      </div>

      {practitionerLabel && (
        <div
          className="truncate text-[11px] font-medium text-palette-slate-badge"
          title={practitionerLabel}
        >
          {practitionerLabel}
        </div>
      )}
    </button>
  );
}

/**
 * Horizontal strip of lab invoice PDF chips. Selection state can be
 * controlled by the parent (pass `selectedAttachmentId` + `onSelect`) so the
 * PDF preview can live in a separate column of the page grid.
 */
export default function LabInvoiceAttachments({
  clinicId,
  date,
  selectedAttachmentId = null,
  onSelect,
  // When embedded (the tabbed attachments panel on the daily report), the host
  // draws the border, heading and toolbar — rendering our own would double them.
  embedded = false,
  // eslint-disable-next-line no-unused-vars
  canAssign = false,
  // eslint-disable-next-line no-unused-vars
  selectedPractitioner = null,
}) {
  const { data, isLoading, isError, error } = useLabInvoiceAttachments(
    { clinic: clinicId, date },
    { enablePolling: true },
  );

  const attachments = useMemo(() => data?.results || [], [data?.results]);

  function handleSelect(attachment) {
    if (typeof onSelect !== "function") return;
    onSelect(selectedAttachmentId === attachment.id ? null : attachment.id, attachment);
  }

  const body = (
    <>
      {!embedded && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-palette-slate-primary">Lab Invoices</h3>
            <p className="text-xs text-palette-slate-secondary">
              {clinicId} - {date}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isLoading && <span className="text-xs text-palette-slate-secondary">Loading...</span>}
            {selectedAttachmentId && (
              <Button type="button" variant="secondary" onClick={() => onSelect?.(null, null)}>
                Close preview
              </Button>
            )}
          </div>
        </div>
      )}

      {isError && (
        <div className="text-sm text-palette-rose-badge">
          {getApiErrorMessage(error, "Unable to load lab invoices.")}
        </div>
      )}

      {!isLoading && !isError && attachments.length === 0 && (
        <div className="text-sm text-palette-slate-secondary">
          No lab invoice PDFs for this date.
        </div>
      )}

      {attachments.length > 0 && (
        <div className="flex gap-2 overflow-x-auto px-0.5 py-1">
          {attachments.map((attachment) => (
            <AttachmentChip
              key={attachment.id}
              attachment={attachment}
              isActive={selectedAttachmentId === attachment.id}
              onSelect={handleSelect}
            />
          ))}
        </div>
      )}
    </>
  );

  if (embedded) return body;

  return (
    <section className="mb-5 rounded-lg border border-palette-slate-divider bg-palette-slate-soft px-4 py-3">
      {body}
    </section>
  );
}

/**
 * Right-column preview: shows the selected attachment's metadata and the PDF
 * inline. Designed to slot into a grid column next to other content
 * (see FinancialReport's report grid).
 *
 * Layout goals:
 * - Sticky on scroll so the PDF stays visible while the user edits the form.
 * - One scroll only (the browser's PDF viewer). Header is fixed at the top of
 *   the panel; the iframe fills the remaining height with `min-h-0` so flex
 *   doesn't overflow its parent.
 * - The PDF is fit-to-width by `LabInvoicePdfFrame` so no inner horizontal
 *   scrollbar is needed.
 */
export function LabInvoiceAttachmentPreview({
  attachmentId,
  clinicId,
  date,
  canAssign = false,
  onClose,
  // Reserve some space for the page chrome (top nav + tabs + card padding).
  // Caller can override with a CSS length if needed.
  height = "calc(100vh - 140px)",
}) {
  const { data } = useLabInvoiceAttachments({ clinic: clinicId, date }, { enablePolling: true });
  const updateAssignment = useUpdateLabInvoiceAttachmentAssignment();
  const [isEditing, setIsEditing] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [draft, setDraft] = useState({ clinic: clinicId, assigned_date: date });

  const attachment = useMemo(() => {
    const list = data?.results || [];
    return list.find((a) => a.id === attachmentId) || null;
  }, [data?.results, attachmentId]);

  // Resolve the underlying PDF URL so we can offer "open in new tab". The
  // hook is shared with `LabInvoicePdfFrame` (same queryKey) — no double fetch.
  const { data: viewUrlData } = useLabInvoiceAttachmentViewUrl(attachmentId, {
    enabled: !!attachmentId,
  });

  if (!attachment) {
    return (
      <div
        className="sticky top-4 flex items-center justify-center rounded-md border border-palette-slate-divider bg-white p-4 text-sm text-palette-slate-secondary"
        style={{ height }}
      >
        Loading invoice...
      </div>
    );
  }

  const senderLabel = attachment.sender_name || attachment.sender_email || "unknown";
  const practitionerLabel =
    attachment.matched_practitioner_name || attachment.practitioner_name || null;
  const subjectLabel = attachment.email_subject || "";

  async function saveAssignment() {
    try {
      await updateAssignment.mutateAsync({ attachmentId: attachment.id, data: draft });
      showSuccess("Lab invoice assignment updated.");
      setIsEditing(false);
      setShowAssign(false);
    } catch (err) {
      showError(getApiErrorMessage(err, "Failed to update lab invoice assignment."));
    }
  }

  return (
    <div
      className="sticky top-4 flex flex-col overflow-hidden rounded-md border border-palette-slate-divider bg-white shadow-sm"
      style={{ height }}
    >
      {/* Compact header: sender / subject in one line + quick actions */}
      <div className="flex shrink-0 items-center gap-2 border-b border-palette-slate-divider bg-palette-slate-soft px-3 py-2">
        <div className="min-w-0 flex-1">
          <div
            className="truncate text-sm font-medium text-palette-slate-primary"
            title={senderLabel}
          >
            {senderLabel}
            {practitionerLabel && (
              <span className="ml-2 rounded bg-palette-indigo-soft px-1.5 py-0.5 text-[11px] font-medium text-palette-indigo-heading">
                {practitionerLabel}
              </span>
            )}
          </div>
          {subjectLabel && (
            <div className="truncate text-[11px] text-palette-slate-secondary" title={subjectLabel}>
              {subjectLabel}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {viewUrlData?.url && (
            <a
              href={viewUrlData.url}
              target="_blank"
              rel="noreferrer noopener"
              title="Open PDF in a new tab"
              className="rounded p-1.5 text-palette-slate-secondary transition hover:bg-palette-slate-divider hover:text-palette-slate-primary"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path d="M11 3a1 1 0 1 0 0 2h2.586l-6.293 6.293a1 1 0 1 0 1.414 1.414L15 6.414V9a1 1 0 1 0 2 0V4a1 1 0 0 0-1-1h-5Z" />
                <path d="M5 5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-3a1 1 0 1 0-2 0v3H5V7h3a1 1 0 0 0 0-2H5Z" />
              </svg>
            </a>
          )}
          {/* {canAssign && (
            <button
              type="button"
              onClick={() => {
                setShowAssign((current) => {
                  const next = !current;
                  if (next) {
                    setIsEditing(true);
                    setDraft({
                      clinic: attachment.clinic || clinicId,
                      assigned_date: attachment.assigned_date || date,
                    });
                  } else {
                    setIsEditing(false);
                  }
                  return next;
                });
              }}
              title="Reassign clinic / date"
              className={`rounded p-1.5 transition hover:bg-palette-slate-divider ${
                showAssign
                  ? "bg-palette-slate-divider text-palette-slate-primary"
                  : "text-palette-slate-secondary hover:text-palette-slate-primary"
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path d="M17.414 2.586a2 2 0 0 0-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 0 0 0-2.828ZM5 6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2v-3a1 1 0 1 0-2 0v3H5V8h3a1 1 0 1 0 0-2H5Z" />
              </svg>
            </button>
          )} */}
          {/* {onClose && (
            <button
              type="button"
              onClick={onClose}
              title="Close preview"
              className="rounded p-1.5 text-palette-slate-secondary transition hover:bg-palette-slate-divider hover:text-palette-slate-primary"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 0 1 1.414 0L10 8.586l4.293-4.293a1 1 0 1 1 1.414 1.414L11.414 10l4.293 4.293a1 1 0 0 1-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 0 1-1.414-1.414L8.586 10 4.293 5.707a1 1 0 0 1 0-1.414Z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )} */}
        </div>
      </div>

      {/* Optional assignment editor — collapsed by default to keep the PDF area large */}
      {canAssign && showAssign && isEditing && (
        <div className="shrink-0 space-y-2 border-b border-palette-slate-divider bg-palette-amber-soft px-3 py-2">
          <div className="grid grid-cols-2 gap-2">
            <label className="block text-[11px] font-medium text-palette-slate-badge">
              Clinic
              <input
                className="mt-1 block w-full rounded border border-palette-slate-ring px-2 py-1 text-sm"
                value={draft.clinic}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, clinic: event.target.value }))
                }
              />
            </label>
            <label className="block text-[11px] font-medium text-palette-slate-badge">
              Assigned date
              <input
                type="date"
                className="mt-1 block w-full rounded border border-palette-slate-ring px-2 py-1 text-sm"
                value={draft.assigned_date}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    assigned_date: event.target.value,
                  }))
                }
              />
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowAssign(false);
                setIsEditing(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="success"
              disabled={updateAssignment.isPending}
              onClick={saveAssignment}
            >
              Save
            </Button>
          </div>
        </div>
      )}

      {/* PDF fills remaining height. min-h-0 lets the flex child shrink so the
          iframe sizes correctly and only the PDF viewer scrolls. */}
      <div className="min-h-0 flex-1 bg-palette-slate-muted">
        <LabInvoicePdfFrame attachmentId={attachment.id} />
      </div>
    </div>
  );
}
